'use client';

/**
 * API namespace `credits`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L5742-L5884).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const credits = {
  /**
   * GET /api/market/credits/balance — current user's credit balance.
   *
   * Returns `CreditBalanceResponse` (`balance`, `totalPurchased`,
   * `totalSpent` — all decimal strings per INV-SEC-02 BigInt-based
   * accounting). The credit balance is a SEPARATE accounting unit from
   * the USDT wallet balance (`api.getWallet`/`api.getDashboard`) —
   * credits can ONLY be spent inside Profileia Market (Fases 4-7) and
   * are NOT refundable/transferable (INV-MKT-02).
   *
   * Authz: any authenticated user. 403 `MARKET_CREDITS_DISABLED` when
   * the `market_credits_enabled` feature flag is off (backend
   * authoritative gate — frontend also hides the page).
   */
  async getBalance(signal?: AbortSignal): Promise<CreditBalanceResponse> {
    return apiFetch<CreditBalanceResponse>('/market/credits/balance', {
      signal,
    });
  },
  /**
   * GET /api/market/credits/history — paginated purchase history.
   *
   * Returns `CreditHistoryResponse` (`purchases: CreditPurchase[]` +
   * `total: number`). The backend clamps `page >= 0` and `limit` to
   * `[1, 100]` (defense in depth — the frontend can send any value and
   * the backend will normalize).
   *
   * Authz: self only — backend verifies `purchase.userId === req.user!.sub`.
   */
  async getHistory(
    page = 0,
    limit = 20,
    signal?: AbortSignal,
  ): Promise<CreditHistoryResponse> {
    const qs = `?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
    return apiFetch<CreditHistoryResponse>(
      `/market/credits/history${qs}`,
      { signal },
    );
  },
  /**
   * GET /api/market/credits/transactions — UNIFIED transaction history.
   *
   * Sprint 86 (Founder directive 2026-09-11): "los usuarios pudiesemos
   * ver el historial de compras de creditos y gastos que hemos hecho
   * con ellas".
   *
   * Unlike `getHistory` (which returns only `CreditPurchase` docs —
   * purchases), this endpoint reads from `UnifiedTransaction` — the
   * comprehensive model that records ALL financial events (purchases,
   * spends, commissions, payouts, orders, services, digitals). This
   * gives the user a single unified view of every credit-related
   * transaction in one place.
   *
   * Returns `CreditTransactionsResponse` (`transactions` + `total` +
   * `page` + `limit`). Each item has a `direction` field ('credit' |
   * 'debit') so the UI can render the amount with the right color +
   * sign (+/-).
   *
   * Authz: self only — backend filters by `userId === req.user!.sub`.
   */
  async getTransactions(
    page = 0,
    limit = 20,
    signal?: AbortSignal,
  ): Promise<CreditTransactionsResponse> {
    const qs = `?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`;
    return apiFetch<CreditTransactionsResponse>(
      `/market/credits/transactions${qs}`,
      { signal },
    );
  },
  /**
   * POST /api/market/credits/purchase — buy credits.
   *
   * Spec ref: SPEC-081 Fase 1 §5.3 (controller), §3.3-§3.6 (service
   *           flows), ADR-039 §2 (thresholds) + §10.3 (payout rates).
   *
   * Authz: `requireAuth` + `requireMarketCreditsEnabled` +
   * `creditPurchaseLimiter` (max 10/h per user — userKey, falls back to
   * ipKey) + `requireRecent2FA` (TOTP/wallet 2FA verified within last
   * 5 min — frontend opens `TwoFactorFinancialDialog` on 403
   * `2FA_REQUIRED`).
   *
   * Response shape varies by `paymentMethod`:
   *   - `saldo_usdt`: `{ purchaseId, status: 'completed', credits,
   *      packageActivated, activatedTier? }` — credits + (optional) free
   *      package activated instantly (USDT debited synchronously).
   *   - `metamask`: `{ purchaseId, status: 'pending', intentId,
   *      depositAddress, expiresAt }` — backend creates a DepositIntent
   *      on the chosen chain; the deposit-listener confirms + credits
   *      asynchronously.
   *   - `crypto_manual`: `{ purchaseId, status: 'pending', intentId }`
   *      — user pre-sent USDT from an external wallet; the deposit-
   *      listener verifies the txHash on-chain + credits when matched.
   *
   * INV-SEC-03: `idempotencyKey` (UUID v4) is the idempotency key —
   * the same key returns the same result without double-crediting
   * (Redis SETNX, 24h TTL). The frontend generates a fresh UUID per
   * purchase attempt via `safeUUID()`.
   */
  async purchase(
    params: CreditPurchaseRequest,
    signal?: AbortSignal,
  ): Promise<CreditPurchaseResponse> {
    return apiFetch<CreditPurchaseResponse>('/market/credits/purchase', {
      method: 'POST',
      body: JSON.stringify(params),
      signal,
    });
  },
};
